import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../utils/factoryUtils";
import {
  AnalisisCompraItem,
  AnalisisGrupoItem,
  AnalisisLaboratorioItem,
  AnalisisNutrienteItem,
  AnalisisParametroItem,
  AnalisisRegistroItem,
  FertilizacionService,
} from "../../../generated/grpc/fertilizacion_pb";
import { grpcFertilizacionDatabase } from "../database/GrpcFertilizacionDatabase";
import { serviceSync } from "../../../base/services/ServiceSync";
import { AnalisisLaboratorioResponse } from "../response/AnalisisLaboratorioResponse";
import { AnalisisRegistroResponse } from "../response/AnalisisRegistroResponse";
import { AnalisisParametroResponse } from "../response/AnalisisParametroResponse";
import { AnalisisNutrienteResponse } from "../response/AnalisisNutrienteResponse";
import { AnalisisGrupoResponse } from "../response/AnalisisGrupoResponse";
import { AnalisisCompraResponse } from "../response/AnalisisCompraResponse";

/** Variante gRPC-Web (con delta real, por finca) de `FertilizacionGetListByFincaIdService`
 *  (ver ese archivo). Devuelve `AnalisisLaboratorioResponse[]` -mismo shape que
 *  ya usa el resto de la app-, así que el swap queda contenido acá. Rellena
 *  con valores inertes los campos que se comprobó que nadie lee en toda la
 *  app (ver el comentario de cada `*Item` en `fertilizacion.proto`). */
class FertilizacionGrpcService {
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<AnalisisLaboratorioResponse[]> {
    await this._syncAsync();
    const items = await grpcFertilizacionDatabase.getListByFincaIdAsync<AnalisisLaboratorioItem>(
      this._fincaId,
    );
    return items.map(toAnalisisLaboratorioResponse);
  }

  clearCache(): void {
    serviceSync.reset(`grpc:fertilizacion:${this._fincaId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:fertilizacion:${this._fincaId}`, async () => {
      const ultimaSincronizacion = await grpcFertilizacionDatabase.getUltimaSincronizacionAsync(
        this._fincaId,
      );
      const client = createClient(FertilizacionService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listByFincaId({
          fincaId: this._fincaId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      await grpcFertilizacionDatabase.putDeltaAsync(
        this._fincaId,
        response.items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por fincaId (comparte cooldown/caché,
 *  mismo criterio que `fx_createFertilizacionGetListByFincaIdService`). */
export const fx_createFertilizacionGrpcService = createCachedFactory(
  (fincaId: string) => new FertilizacionGrpcService(fincaId),
);

/** Adapta un `AnalisisLaboratorioItem` (gRPC) al shape `AnalisisLaboratorioResponse`
 *  (REST) — ver el comentario de la clase. Exportada porque los servicios de
 *  guardado/borrado también la necesitan. */
export function toAnalisisLaboratorioResponse(
  analisis: AnalisisLaboratorioItem,
): AnalisisLaboratorioResponse {
  return {
    id: analisis.id,
    isEliminado: analisis.eliminado,
    fincaId: analisis.fincaId,
    fechaAnalisis: analisis.fechaAnalisis,
    tipoAnalisis: analisis.tipoAnalisis,
    nombreLaboratorio: analisis.nombreLaboratorio,
    blobUrl: "",
    nombreArchivo: analisis.nombreArchivo,
    totalRegistros: analisis.totalRegistros,
    kmlLoaded: false,
    pendingLotsCount: analisis.pendingLotsCount,
    recomendacionFecha: analisis.recomendacionFecha == null ? null : Number(analisis.recomendacionFecha),
    recomendacionThresholdPct: null,
    registros: analisis.registros.map(toAnalisisRegistroResponse),
    grupos: analisis.grupos.map(toAnalisisGrupoResponse),
    compra: analisis.compra.map(toAnalisisCompraResponse),
  };
}

function toAnalisisRegistroResponse(
  registro: AnalisisRegistroItem,
): AnalisisRegistroResponse {
  return {
    id: registro.id,
    loteId: registro.loteId ?? null,
    loteResolutionStatus: "",
    cropResolutionStatus: registro.cropResolutionStatus,
    cropRaw: registro.cropRaw ?? null,
    crop: registro.crop ?? null,
    cropSource: null,
    isDuplicate: false,
    idMuestra: registro.idMuestra,
    loteExcel: registro.loteExcel,
    finca: "",
    propietario: "",
    municipio: "",
    fechaAnalisisExcel: "",
    advertencias: [],
    alertas: registro.alertas,
    parametros: registro.parametros.map(toAnalisisParametroResponse),
    grupoId: registro.grupoId ?? null,
    esReferencia: registro.esReferencia,
    severityLevel: registro.severityLevel ?? null,
    severityReasons: registro.severityReasons,
    planSummary: registro.planSummary ?? null,
    planAreaHa: registro.planAreaHa ?? null,
    nutrientes: registro.nutrientes.map(toAnalisisNutrienteResponse),
  };
}

function toAnalisisParametroResponse(
  parametro: AnalisisParametroItem,
): AnalisisParametroResponse {
  return {
    nombre: parametro.nombre,
    valor: parametro.valor ?? null,
    unidad: parametro.unidad,
    estado: parametro.estado ?? null,
    color: parametro.color ?? null,
    nota: null,
    isSentinel: parametro.isSentinel,
    isCategorical: parametro.isCategorical,
    textValue: parametro.textValue ?? null,
    rangoMin: null,
    rangoMax: null,
    rangoTexto: parametro.rangoTexto ?? null,
  };
}

function toAnalisisNutrienteResponse(
  nutriente: AnalisisNutrienteItem,
): AnalisisNutrienteResponse {
  return {
    nutrient: nutriente.nutrient,
    nutrientType: nutriente.nutrientType,
    kgHa: nutriente.kgHa,
    kgTotal: nutriente.kgTotal,
    status: "",
    tier: nutriente.tier,
    urgency: nutriente.urgency,
    fractions: 0,
    timing: null,
    note: nutriente.note ?? null,
    efficiencyWarning: nutriente.efficiencyWarning ?? null,
    estimatedEfficiencyPct: nutriente.estimatedEfficiencyPct ?? null,
    sourceParameter: nutriente.sourceParameter ?? null,
    sourceParameters: nutriente.sourceParameters,
  };
}

function toAnalisisGrupoResponse(grupo: AnalisisGrupoItem): AnalisisGrupoResponse {
  return {
    id: grupo.id,
    groupId: "",
    groupType: "",
    totalAreaHa: grupo.totalAreaHa,
    nutrientsSummary: {},
    notes: [],
    nitrofertPlan: grupo.nitrofertPlan ? JSON.parse(grupo.nitrofertPlan) : null,
    l3Error: grupo.l3Error ?? null,
  };
}

function toAnalisisCompraResponse(compra: AnalisisCompraItem): AnalisisCompraResponse {
  return {
    product: compra.product,
    line: compra.line ?? null,
    clFree: compra.clFree,
    kgTotal: compra.kgTotal,
    bags50Kg: compra.bags50Kg,
    lots: compra.lots,
    loteIds: compra.loteIds,
    areaConsumidaHa: compra.areaConsumidaHa,
  };
}
